import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Markdown } from '../../components/Markdown';
import { ErrorNote, ReviewChip, SectionTitle, Spinner, fmtDate } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import type { EngagementBundle } from '../EngagementDetail';

const GENERATORS = [
  { type: 'pre_call_brief', label: 'Pre-call brief', hint: 'From the intake submission' },
  { type: 'maturity_report', label: 'Maturity report', hint: 'Needs ≥1 scored dimension' },
  { type: 'usecase_roadmap', label: 'Opportunity roadmap', hint: 'Needs ≥1 use case' },
  { type: 'nist_gap_assessment', label: 'NIST gap assessment', hint: 'Needs ≥1 NIST mapping' },
] as const;

export function DeliverablesTab({ bundle, reload }: { bundle: EngagementBundle; reload: () => Promise<void> }) {
  const { user } = useAuth();
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [content, setContent] = useState<Record<string, { content_md: string; ai_engine: string | null; template_version: string | null; source_inputs: string | null; review_status: string; reviewed_by: string | null; approved_by: string | null }>>({});

  async function generate(type: string) {
    setGenerating(type);
    setError('');
    try {
      const r = await api.post<{ artifactId: string }>(`/api/engagements/${bundle.engagement.id}/deliverables/${type}`);
      await reload();
      await open(r.artifactId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 412) setError(err.body.message as string);
      else setError('Generation failed.');
    } finally {
      setGenerating(null);
    }
  }

  async function open(id: string) {
    if (!content[id]) {
      const r = await api.get<{ artifact: typeof content[string] & { id: string } }>(`/api/artifacts/${id}`);
      setContent((c) => ({ ...c, [id]: r.artifact }));
    }
    setOpenId(id);
  }

  async function setReview(id: string, review_status: string) {
    try {
      const r = await api.patch<{ artifact: typeof content[string] }>(`/api/artifacts/${id}`, { review_status });
      setContent((c) => ({ ...c, [id]: r.artifact }));
      await reload();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'Approval requires an admin.' : 'Update failed.');
    }
  }

  async function copyMd(id: string) {
    const md = content[id]?.content_md;
    if (md) await navigator.clipboard.writeText(md);
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const openArtifact = openId ? content[openId] : null;
  const openMeta = bundle.artifacts.find((a) => a.id === openId);

  return (
    <div>
      <SectionTitle>Generate deliverables</SectionTitle>
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        {GENERATORS.map((g) => (
          <button
            key={g.type}
            onClick={() => void generate(g.type)}
            disabled={generating !== null}
            className="card !p-4 text-left transition-colors hover:border-brand-600 disabled:opacity-60"
          >
            <div className="font-semibold text-slate-100">{generating === g.type ? '✨ Generating…' : g.label}</div>
            <div className="mt-0.5 text-xs text-slate-500">{g.hint}</div>
          </button>
        ))}
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Deliverables render deterministically from engagement data with an AI executive narrative
        (engine + template version recorded). Markdown exports import cleanly into Google Docs.
        Drafts carry an internal watermark until approved.
      </p>
      {error && <ErrorNote message={error} />}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-2 lg:col-span-1">
          {bundle.artifacts.length === 0 ? (
            <div className="card py-10 text-center text-sm text-slate-500">No artifacts yet.</div>
          ) : (
            bundle.artifacts.map((a) => (
              <button
                key={a.id}
                onClick={() => void open(a.id)}
                className={`card block w-full !p-3 text-left transition-colors hover:border-brand-600 ${openId === a.id ? 'border-brand-600' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-100">{a.title}</span>
                  <ReviewChip status={a.review_status} />
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {a.type} · {a.ai_engine ?? 'none'} · v{a.template_version} · {fmtDate(a.created_at)}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {openId && !openArtifact && <Spinner />}
          {openArtifact && (
            <div className="card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2">
                  <ReviewChip status={openArtifact.review_status} />
                  <span className="text-xs text-slate-500">
                    engine: {openArtifact.ai_engine ?? 'none'} · template v{openArtifact.template_version}
                    {openArtifact.reviewed_by ? ` · reviewed by ${openArtifact.reviewed_by}` : ''}
                    {openArtifact.approved_by ? ` · approved by ${openArtifact.approved_by}` : ''}
                  </span>
                </div>
                <div className="flex gap-2">
                  <a className="btn-secondary !px-2.5 !py-1 text-xs" href={`/api/artifacts/${openId}/download`}>⬇ .md</a>
                  <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => void copyMd(openId!)}>Copy</button>
                  {openArtifact.review_status === 'draft' && (
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => void setReview(openId!, 'in_review')}>Send to review</button>
                  )}
                  {openArtifact.review_status !== 'approved' && isAdmin && (
                    <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => void setReview(openId!, 'approved')}>✓ Approve</button>
                  )}
                </div>
              </div>
              <div className="max-h-[70vh] overflow-y-auto pr-2">
                <Markdown content={openArtifact.content_md} />
              </div>
            </div>
          )}
          {!openId && bundle.artifacts.length > 0 && (
            <div className="card py-16 text-center text-sm text-slate-500">Select an artifact to view it.</div>
          )}
        </div>
      </div>
    </div>
  );
}
